export function currentYQ(): string {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `${y}Q${q}`;
}

export function latestEndedYQ(): string {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.floor(now.getMonth() / 3) + 1;
    const endedYear = q === 1 ? y - 1 : y;
    const endedQ = q === 1 ? 4 : q - 1;
    return `${endedYear}Q${endedQ}`;
}

export function generateYQsFromEnv(envValue?: string): string[] {
    const raw = (envValue ?? process.env.REACT_APP_YQ_RANGES ?? "").trim();

    const [cy, cq] = splitYQ(currentYQ());
    const fallback = buildRange(2013, 2, cy, cq); // default 2013Q2 -> CURRENT

    if (!raw) return fallback;

    try {
        const tokens = raw.split(",").map((t) => t.trim()).filter(Boolean);
        const combined = uniqueSortedYQs(tokens.flatMap(expandToken));
        return combined.length ? combined : fallback;
    } catch {
        return fallback;
    }
}

// ---------------- internal ----------------
function buildRange(startY: number, startQ: number, endY: number, endQ: number): string[] {
    const out: string[] = [];
    for (let y = startY; y <= endY; y++) {
        const qStart = y === startY ? startQ : 1;
        const qEnd = y === endY ? endQ : 4;
        for (let q = qStart; q <= qEnd; q++) out.push(`${y}Q${q}`);
    }
    return out;
}

function splitYQ(yq: string): [number, number] {
    const [y, q] = yq.split("Q").map(Number);
    return [y, q];
}

function parsePart(partRaw: string, bound: "start" | "end"): { y: number; q: number } | null {
    const part = partRaw.trim().toUpperCase();

    if (part === "CURRENT") {
        const [y, q] = splitYQ(currentYQ());
        return { y, q };
    }
    if (part === "LATEST_ENDED") {
        const [y, q] = splitYQ(latestEndedYQ());
        return { y, q };
    }

    // Accept "YYYYQn" or "YYYY"
    const m = part.match(/^(\d{4})(?:Q([1-4]))?$/);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const q = m[2] ? parseInt(m[2], 10) : bound === "start" ? 1 : 4;
    return { y, q };
}

function expandToken(tokenRaw: string): string[] {
    const token = tokenRaw.trim();
    if (!token) return [];

    if (!token.includes("-")) {
        const p = parsePart(token, "start");
        return p ? [`${p.y}Q${p.q}`] : [];
    }

    const [aRaw, bRaw] = token.split("-", 2);
    const a = aRaw ? parsePart(aRaw, "start") : parsePart("2013Q2", "start");
    const b = bRaw ? parsePart(bRaw, "end") : parsePart("CURRENT", "end");
    if (!a || !b) return [];
    if (a.y > b.y || (a.y === b.y && a.q > b.q)) return [];
    return buildRange(a.y, a.q, b.y, b.q);
}

function uniqueSortedYQs(list: string[]): string[] {
    const seen = new Set(list);
    const arr = [...seen];
    arr.sort((A, B) => {
        const [yA, qA] = splitYQ(A);
        const [yB, qB] = splitYQ(B);
        return yA - yB || qA - qB;
    });
    return arr;
}
